import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdGroupAdd, MdGroups, MdDelete, MdVisibility } from 'react-icons/md';
import { gruposAPI } from '../services/api';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';
import { EmptyState } from '../components/SkeletonLoader';
import DeleteGroupModal from '../components/DeleteGroupModal';

const Container = styled.div`
  max-width: 1100px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.xl};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const Button = styled.button`
  padding: 10px ${p => p.theme.spacing.lg};
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  transition: ${p => p.theme.transitions.fast};
  cursor: pointer;

  svg { font-size: 1.2rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${p => p.theme.colors.primary}40;
  }
`;

const ListSection = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: ${p => p.theme.spacing.md};
    border-radius: ${p => p.theme.borderRadius.md};
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
`;

const Th = styled.th`
  padding: 8px ${p => p.theme.spacing.md};
  text-align: left;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const Td = styled.td`
  padding: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
`;

const Row = styled.tr`
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  &:hover td { background: ${p => p.theme.colors.background}; }
`;

const GroupNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const GroupIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg { font-size: 1.1rem; }
`;

const GroupNameText = styled.span`
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
`;

const Balance = styled.span`
  font-weight: 700;
  color: ${p => p.hasBalance ? p.theme.colors.danger : p.theme.colors.success};
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 5px 14px;
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px ${p => p.theme.colors.primary}40;
  }
`;

const DeleteBtn = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  color: ${p => p.theme.colors.textSecondary};
  cursor: pointer;
  flex-shrink: 0;
  transition: ${p => p.theme.transitions.fast};

  svg { font-size: 1rem; }

  &:hover {
    background: ${p => p.theme.colors.danger}15;
    border-color: ${p => p.theme.colors.danger};
    color: ${p => p.theme.colors.danger};
  }
`;

/* ── Mobile card list ─────────────────────────────────── */
const MobileCardList = styled.div`
  display: none;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

const GroupCard = styled.div`
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;

  &:active { transform: scale(0.985); }
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
`;

const CardMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 3px;
`;

const CardRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
`;

const Grupos = () => {
  const navigate = useNavigate();
  const { toast } = useModal();
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin';

  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadGrupos();
  }, []);

  const loadGrupos = async () => {
    try {
      setLoading(true);
      const res = await gruposAPI.getAll();
      setGrupos(res.data.data || []);
    } catch (err) {
      console.error('Error al cargar grupos:', err);
      toast('Error al cargar los grupos de clientes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarClick = (e, grupo) => {
    e.stopPropagation();
    setDeleteTarget(grupo);
  };

  const handleConfirmEliminar = async (accion) => {
    const grupo = deleteTarget;
    setDeleteTarget(null);
    try {
      await gruposAPI.delete(grupo.id, accion);
      toast('Grupo eliminado exitosamente', 'success');
      loadGrupos();
    } catch (err) {
      toast('Error al eliminar el grupo: ' + (err.message || 'Error desconocido'), 'error');
    }
  };

  return (
    <Container>
      <PageHeader>
        <Title>Grupos de Clientes</Title>
        <Button onClick={() => navigate('/grupos/nuevo')}>
          <MdGroupAdd />
          Nuevo Grupo
        </Button>
      </PageHeader>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-light)' }}>Cargando...</div>
      ) : grupos.length === 0 ? (
        <EmptyState
          type="clients"
          title="Sin grupos aún"
          subtitle="Crea un grupo para agrupar varios clientes (familiares o de una misma empresa) y ver su saldo combinado."
        />
      ) : (
        <ListSection>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Grupo</Th>
                  <Th>Clientes</Th>
                  <Th>Saldo Pendiente</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {grupos.map(grupo => {
                  const saldo = parseFloat(grupo.saldo_pendiente_total || 0);
                  return (
                    <Row key={grupo.id} onClick={() => navigate(`/grupos/${grupo.id}`)}>
                      <Td>
                        <GroupNameCell>
                          <GroupIcon><MdGroups /></GroupIcon>
                          <GroupNameText>{grupo.nombre}</GroupNameText>
                        </GroupNameCell>
                      </Td>
                      <Td>{grupo.total_clientes} cliente{grupo.total_clientes === 1 ? '' : 's'}</Td>
                      <Td>
                        <Balance hasBalance={saldo > 0}>
                          ${saldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </Balance>
                      </Td>
                      <Td>
                        <RowActions>
                          <ActionButton onClick={(e) => { e.stopPropagation(); navigate(`/grupos/${grupo.id}`); }}>
                            <MdVisibility /> Ver
                          </ActionButton>
                          {isAdmin && (
                            <DeleteBtn onClick={(e) => handleEliminarClick(e, grupo)} title="Eliminar grupo">
                              <MdDelete />
                            </DeleteBtn>
                          )}
                        </RowActions>
                      </Td>
                    </Row>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>

          <MobileCardList>
            {grupos.map(grupo => {
              const saldo = parseFloat(grupo.saldo_pendiente_total || 0);
              return (
                <GroupCard key={grupo.id} onClick={() => navigate(`/grupos/${grupo.id}`)}>
                  <GroupIcon><MdGroups /></GroupIcon>
                  <CardBody>
                    <CardName>{grupo.nombre}</CardName>
                    <CardMeta>{grupo.total_clientes} cliente{grupo.total_clientes === 1 ? '' : 's'}</CardMeta>
                  </CardBody>
                  <CardRight>
                    <Balance hasBalance={saldo > 0}>
                      ${saldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </Balance>
                    {isAdmin && (
                      <DeleteBtn onClick={(e) => handleEliminarClick(e, grupo)} title="Eliminar grupo">
                        <MdDelete />
                      </DeleteBtn>
                    )}
                  </CardRight>
                </GroupCard>
              );
            })}
          </MobileCardList>
        </ListSection>
      )}

      {deleteTarget && (
        <DeleteGroupModal
          grupo={deleteTarget}
          onConfirm={handleConfirmEliminar}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </Container>
  );
};

export default Grupos;
